import React from 'react';
import { Tooltip, Typography, theme } from 'antd';

const { Text } = Typography;

export interface CheckerStats {
  queueDepth: number;
  activeWorkers: number;
  busyWorkers: number;
  throttled?: boolean;
}

// The checker state is one line of text, so it rides in the page title row
// instead of owning a band of its own: four facts are not worth 40px of a
// 800px-tall screen.
const CheckerStatsLine: React.FC<{ stats: CheckerStats; total: number }> = ({ stats, total }) => {
  const { token } = theme.useToken();
  const { busyWorkers, activeWorkers, queueDepth, throttled } = stats;
  const isIdle = busyWorkers === 0 && queueDepth === 0;

  const sep = <Text type="secondary" style={{ fontSize: 12 }}>·</Text>;

  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
      <Tooltip title={isIdle ? 'Checker idle — waiting for next run' : `${busyWorkers} of ${activeWorkers} workers processing checks`}>
        <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>
          Workers{' '}
          <span style={{ color: busyWorkers > 0 ? token.colorSuccess : token.colorTextTertiary, fontWeight: 600 }}>
            {busyWorkers}
          </span>
          <span style={{ color: token.colorTextQuaternary }}>/{activeWorkers}</span>
        </Text>
      </Tooltip>
      {sep}
      <Tooltip title={queueDepth > 0 ? `${queueDepth} check jobs waiting in queue` : 'No jobs in queue'}>
        <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>
          Queue{' '}
          <span style={{ color: queueDepth > 0 ? token.colorWarning : token.colorSuccess, fontWeight: 600 }}>
            {queueDepth}
          </span>
        </Text>
      </Tooltip>
      {sep}
      <Tooltip title="Total monitored servers">
        <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>
          Servers <span style={{ color: token.colorText, fontWeight: 600 }}>{total}</span>
        </Text>
      </Tooltip>
      {sep}
      {throttled ? (
        <Tooltip title="Workers auto-reduced due to Redis connection pressure. Will recover automatically.">
          <Text style={{ fontSize: 12, fontWeight: 600, color: token.colorError, cursor: 'help' }}>⚠ Throttled</Text>
        </Tooltip>
      ) : isIdle ? (
        <Tooltip title="Waiting for next scheduled run or manual trigger">
          <Text type="secondary" style={{ fontSize: 12, cursor: 'help' }}>○ Idle</Text>
        </Tooltip>
      ) : (
        <Tooltip title="Health checks are actively running">
          <Text style={{ fontSize: 12, fontWeight: 600, color: token.colorSuccess, cursor: 'help' }}>● Checking</Text>
        </Tooltip>
      )}
    </span>
  );
};

export default CheckerStatsLine;
